import React, { useState } from "react";
import "./StoneGame.css";

const switches = {
  1: { A: +1, C: +1 },
  2: { B: +1, C: +1 },
  3: { B: +1, D: +1 },
  4: { A: +1, C: +1, E: +1 },
  5: { D: +1, E: +1 },
};

const stoneRanges = {
  A: [1, 3],
  B: [3, 5],
  C: [2, 4],
  D: [1, 3],
  E: [3, 5],
};

const GLOBAL_MIN = 1;
const GLOBAL_MAX = 5;

function wrapValue(value, range) {
  const [min, max] = range;
  if (value > max) return min;
  if (value < min) return max;
  return value;
}

function pressSwitch(stones, switchNumber) {
  const changes = switches[switchNumber];
  const newStones = { ...stones };
  for (const stone in changes) {
    newStones[stone] = wrapValue(newStones[stone] + changes[stone], stoneRanges[stone]);
  }
  return newStones;
}

function isGoalState(stones) {
  return Object.values(stones).every((value) => value === 3);
}

function findOptimalSequence(initialStones) {
  const maxSteps = 100;
  let queue = [{ stones: initialStones, sequence: [] }];

  for (let i = 0; i < maxSteps; i++) {
    let nextQueue = [];
    for (const { stones, sequence } of queue) {
      if (isGoalState(stones)) {
        return sequence;
      }
      for (let switchNumber = 1; switchNumber <= 5; switchNumber++) {
        const newStones = pressSwitch(stones, switchNumber);
        nextQueue.push({
          stones: newStones,
          sequence: [...sequence, switchNumber],
        });
      }
    }
    queue = nextQueue;
  }

  return null;
}

const StoneGame = () => {
  const [initialStones, setInitialStones] = useState({
    A: 1,
    B: 5,
    C: 2,
    D: 3,
    E: 3,
  });
  const [stones, setStones] = useState(initialStones);
  const [optimalSequence, setOptimalSequence] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // Manage button disabled state

  const handleInputChange = (stone, value) => {
    const range = stoneRanges[stone];
    setInitialStones({
      ...initialStones,
      [stone]: Math.max(range[0], Math.min(range[1], Number(value))),
    });
    setStones({
      ...stones,
      [stone]: Math.max(range[0], Math.min(range[1], Number(value))),
    });
    setOptimalSequence([]);
  };

  const resetGame = () => {
    setStones(initialStones);
    setOptimalSequence([]);
    setIsButtonDisabled(false); // Re-enable the calculate button
  };

  const calculateOptimalSequence = () => {
    setIsButtonDisabled(true); // Disable the button
    const result = findOptimalSequence(initialStones);
    setOptimalSequence(result || []);
  };

  return (
    <div className="stone-game-container">
      <div className="title-container">
        <h1>守護者 レディナイト カマルーシア</h1>
        <h2>~ THRONE AND LIBERTY ~</h2>
      </div>
      <div className="input-container">
        <p className="input-description">
          初期状態を入力してください。
          <br />
          左の石から順にABCDEとしています。
        </p>
        {["A", "B", "C", "D", "E"].map((stone) => (
          <label key={stone} className="stone-input-label">
            {stone}
            <input
              type="number"
              className="stone-input"
              value={initialStones[stone]}
              onChange={(e) => handleInputChange(stone, e.target.value)}
            />
          </label>
        ))}
      </div>
      <div className="optimal-sequence">
        <button
          className="calculate-button"
          onClick={calculateOptimalSequence}
          disabled={isButtonDisabled} // Disable button based on state
        >
          最短手順を計算
        </button>
        {optimalSequence.length > 0 ? (
          <p>
            最短手順: <span>{optimalSequence.join(", ")}</span> (操作回数:{" "}
            <span>{optimalSequence.length}</span>)
          </p>
        ) : optimalSequence.length === 0 ? (
          <p>まだ計算していません。</p>
        ) : (
          <p>解が見つかりませんでした。</p>
        )}
      </div>
      <div className="stones-display">
        {Object.entries(stones).map(([stone, value]) => {
          const normalizedHeight =
            ((value - GLOBAL_MIN) / (GLOBAL_MAX - GLOBAL_MIN)) * 100;
          return (
            <div key={stone} className="stone-container">
              <div className="stone" style={{ height: "150px" }}>
                <div
                  className="stone-fill"
                  style={{
                    height: `${normalizedHeight}%`,
                  }}
                >
                  {"●".repeat(value)}
                </div>
              </div>
              <span className="stone-label">
                {stone}: {value} ({stoneRanges[stone][0]}~{stoneRanges[stone][1]})
              </span>
            </div>
          );
        })}
      </div>
      <div className="buttons-container">
        {Object.keys(switches).map((switchNumber) => (
          <button
            key={switchNumber}
            className="switch-button"
            onClick={() => setStones(pressSwitch(stones, switchNumber))}
          >
            スイッチ{switchNumber}
          </button>
        ))}
      </div>
      <button className="reset-button" onClick={resetGame}>
        リセット
      </button>
    </div>
  );
};

export default StoneGame;
